import { useState, type FormEvent } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { register as apiRegister } from '../api/auth'
import { CheckCircle } from 'lucide-react'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'
import { AuthLayout, AuthError } from '../components/layout/AuthLayout'

export function Register() {
  const navigate = useNavigate()
  const [form, setForm] = useState({ title: '', email: '', registrationId: '', fullName: '', phone: '' })
  const [error,   setError]   = useState('')
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }))

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await apiRegister(form)
      setSuccess(true)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      setError(msg ?? 'Registration failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <AuthLayout
        title="Check your email"
        subtitle={<>We sent a verification link to <strong className="font-medium text-gray-800">{form.email}</strong>. Click it to activate your account and receive your password.</>}
      >
        <div className="mb-8 flex h-14 w-14 items-center justify-center rounded-full bg-success-50">
          <CheckCircle size={28} className="text-success-500" />
        </div>
        <Button size="sm" className="w-full" onClick={() => navigate('/login')}>Back to sign in</Button>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Register Business"
      subtitle="Create a new tenant to get started."
      footer={<>Already have an account?{' '}<Link to="/login" className="text-brand-500 hover:text-brand-600">Sign in</Link></>}
    >
      {error && <AuthError>{error}</AuthError>}
      <form onSubmit={handleSubmit} className="space-y-5">
        {(
          [
            { key: 'title',          label: 'Company name',    placeholder: 'Acme Corp',         type: 'text'  },
            { key: 'registrationId', label: 'Registration ID', placeholder: 'REG-001',           type: 'text'  },
            { key: 'fullName',       label: 'Admin full name', placeholder: 'Jane Doe',          type: 'text'  },
            { key: 'email',          label: 'Admin email',     placeholder: 'admin@company.com', type: 'email' },
            { key: 'phone',          label: 'Phone',           placeholder: '+1 555 000 0000',   type: 'tel'   },
          ] as const
        ).map(({ key, label, placeholder, type }) => (
          <Field key={key} label={label}>
            <input type={type} required value={form[key]} onChange={set(key)}
              placeholder={placeholder} className="field" />
          </Field>
        ))}
        <Button type="submit" size="sm" disabled={loading} loading={loading} className="w-full">
          {loading ? 'Creating…' : 'Create account'}
        </Button>
      </form>
    </AuthLayout>
  )
}
